import React from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import uniqueId from 'lodash/uniqueId'
import {
  line as d3Line,
  area as d3Area,
  curveStep,
  curveMonotoneX,
  curveMonotoneY,
  curveNatural,
  curveLinear
} from 'd3-shape'

const styles = {
  step: curveStep,
  monotoneX: curveMonotoneX,
  monotoneY: curveMonotoneY,
  natural: curveNatural,
  linear: curveLinear
}

export default class Line extends React.PureComponent {
  static propTypes = {
    color: PropTypes.string,
    dashed: PropTypes.bool,
    selected: PropTypes.bool,
    area: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.string
    ]),
    areaData: PropTypes.array,
    invertAreaColors: PropTypes.bool,
    selectedValueIndex: PropTypes.number,
    data: PropTypes.array,
    height: PropTypes.number,
    noPoints: PropTypes.bool,
    noStroke: PropTypes.bool,
    opacity: PropTypes.number,
    strokeWidth: PropTypes.oneOf(['thin', 'thick']),
    style: PropTypes.oneOf(Object.keys(styles))
  }

  static defaultProps = {
    style: 'monotoneX',
    strokeWidth: 'thin',
    invertAreaColors: false
  }

  componentWillMount() {
    this._uniqClipId = uniqueId()
  }

  render() {
    const {color, selected, dashed, strokeWidth, invertAreaColors} = this.props

    const className = classnames('chart_line', {
      [`is-${color}`]: color,
      [`is-${strokeWidth}`]: !!strokeWidth,
      'is-area-inverted': invertAreaColors,
      'is-dashed': dashed,
      'is-selected': selected === true
    })

    return (
      <g className={className}>
        {this.renderPoints()}
        {this.renderLine()}
        {this.renderArea()}
      </g>
    )
  }

  renderLine() {
    const {data, opacity, noStroke} = this.props
    if (noStroke) return null
    const path = this.getLinePath(data)

    return <path d={path} opacity={opacity} className='chart_line-line' />
  }

  renderArea() {
    const {area, height} = this.props

    if (area && height) {
      if (area === true) {
        return this.renderSimpleArea()
      } else {
        return this.renderDiffArea()
      }
    } else {
      return null
    }
  }

  renderSimpleArea() {
    const {data, opacity, height} = this.props
    const {_uniqClipId} = this

    return (
      <g opacity={opacity}>
        <path
          d={this.getAreaPath(data, height)}
          className='chart_line-area'
          fill={`url(#pattern-stripe_${_uniqClipId})`}
        />
      </g>
    )
  }

  renderDiffArea() {
    const {data, areaData, height, opacity} = this.props
    const {_uniqClipId} = this
    const mergedData = data.reduce((acc, item, index) => {
      if (areaData[index]) {
        return acc.concat({
          ...item,
          y2: areaData[index].y
        })
      } else {
        return acc
      }
    }, [])

    return (
      <g opacity={opacity}>
        <clipPath id={`clip-above_${_uniqClipId}`}>
          <path d={this.getAreaPath(mergedData, height)} fill='black' stroke='none' />
        </clipPath>
        <clipPath id={`clip-below_${_uniqClipId}`}>
          <path d={this.getAreaPath(mergedData, 0)} fill='black' stroke='none' />
        </clipPath>
        <path
          clipPath={`url(#clip-above_${_uniqClipId})`}
          d={this.getAreaPath(mergedData)}
          className='chart_line-area-above'
        />
        <path
          clipPath={`url(#clip-below_${_uniqClipId})`}
          d={this.getAreaPath(mergedData)}
          className='chart_line-area-below'
        />
      </g>
    )
  }

  renderPoints() {
    const {data, noPoints, selectedValueIndex, opacity} = this.props
    if (noPoints || (!selectedValueIndex && selectedValueIndex !== 0)) return null

    return (
      <g className='chart_line-points' opacity={opacity}>
        {data.map((item, index) => {
          if (selectedValueIndex === index) {
            return <circle cx={item.x} cy={item.y} r='4' className='chart_line-point' key={index} />
          } else {
            return null
          }
        })}
      </g>
    )
  }

  getLinePath(data) {
    const {style} = this.props

    return d3Line()
      .x(d => d.x)
      .y(d => d.y)
      .defined(d => !!d)
      .curve(styles[style])(data)
  }

  getAreaPath(data, y) {
    const {style} = this.props

    return d3Area()
      .x(d => d.x)
      .y1(d => d.y)
      .y0(d => y === undefined ? d.y2 : y)
      .defined(d => !!d)
      .curve(styles[style])(data)
  }
}



// WEBPACK FOOTER //
// ./client/components/Chart/Line.js